import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Heart, ShoppingBag, Truck, Shield, RotateCcw } from 'lucide-react';
import gsap from 'gsap';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import SanityAPI from '../utils/sanityAPI';

export default function ProductDetailPage() {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedExtraIds, setSelectedExtraIds] = useState([]);
  const [previewExtra, setPreviewExtra] = useState(null);

  // State for product data
  const [product, setProduct] = useState(() => {
    console.log('🔍 PDP: Initializing product state');

    try {
      const cachedProducts = localStorage.getItem('products_cache');
      if (cachedProducts) {
        const products = JSON.parse(cachedProducts);
        const cachedProduct = products.find(p => String(p.id) === String(id));

        if (cachedProduct) {
          console.log(`✅ PDP: Found product in cache: ${cachedProduct.name}`);
          return cachedProduct;
        } else {
          console.warn(`⚠️ PDP: Product ID ${id} not found in cache`);
          console.log(`📦 Available IDs in cache: ${products.map(p => p.id).join(', ')}`);
        }
      }
    } catch (e) {
      console.error('❌ PDP: Failed to parse cache:', e);
      localStorage.removeItem('products_cache');
    }

    console.log('⚠️ PDP: No cached product, will fetch from API');
    return null;
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const imageRef = useRef(null);
  const detailsRef = useRef(null);
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  // Use optional chaining to prevent crash if product is null
  const isProductFavorite = product?.id ? isFavorite(product.id) : false;


  // 2. Fetch fresh data from Sanity (Stale-While-Revalidate)
  useEffect(() => {
    const fetchProduct = async () => {
      console.log('='.repeat(80));
      console.log(`🔵 PDP: Fetching product data for ID: ${id}`);

      try {
        // Use the new endpoint that handles both UUID and integer IDs
        const fetchedProduct = await SanityAPI.getProduct(id);

        if (fetchedProduct) {
          console.log(`✅ PDP: Successfully fetched product: ${fetchedProduct.name}`);

          // Normalize Sanity data
          const normalizedProduct = {
            ...fetchedProduct,
            // Normalize image fields
            image: fetchedProduct.image || fetchedProduct.imageUrl,
            images: fetchedProduct.images || (fetchedProduct.imageUrl ? [fetchedProduct.imageUrl] : []),
          };

          console.log('🔄 PDP: Setting normalized product');
          console.log('='.repeat(80));
          setProduct(normalizedProduct);
          setError(null);
        } else {
          console.warn(`❌ PDP: Product not found for ID: ${id}`);
          console.log('='.repeat(80));
          setError('Product not found');
        }
      } catch (err) {
        console.error('❌ PDP: Failed to fetch product from Sanity:', err);
        console.log('='.repeat(80));
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);


  const handleAddToCart = () => {
    if (product) {
      const images = product.images || (product.image ? [product.image] : []);
      const selected_image_url = images[selectedImage] || product.image || null;
      addToCart(product, quantity, {
        selected_image_url,
        selected_extra_ids: Array.isArray(selectedExtraIds) ? selectedExtraIds : [],
      });
    }
  };

  const handleToggleFavorite = () => {
    if (product) {
      toggleFavorite(product);
    }
  };

  // GSAP animations temporarily disabled for debugging
  // useEffect(() => {
  //   if (!loading && product && imageRef.current && detailsRef.current) {
  //     gsap.fromTo(
  //       imageRef.current,
  //       { opacity: 0, x: -50 },
  //       { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out' }
  //     );
  //     gsap.fromTo(
  //       detailsRef.current,
  //       { opacity: 0, x: 50 },
  //       { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out', delay: 0.2 }
  //     );
  //   }
  // }, [loading, product]);

  // useEffect(() => {
  //   if (imageRef.current) {
  //     gsap.fromTo(
  //       imageRef.current,
  //       { opacity: 0.7, scale: 0.98 },
  //       { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' }
  //     );
  //   }
  // }, [selectedImage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-4 bg-gray-300 w-48 mb-4 rounded"></div>
          <div className="h-64 w-64 bg-gray-300 rounded-xl mb-4"></div>
          <div className="h-4 bg-gray-300 w-32 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="font-manrope font-bold text-2xl mb-4 text-gray-800">
            {error || 'Ürün bulunamadı'}
          </h2>
          <p className="text-gray-600 mb-6">Aradığınız ürün mevcut değil veya kaldırılmış olabilir.</p>
          <Link to="/" className="inline-block bg-trendyol-orange text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-600 transition-colors">
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  // Handle potential missing images array in Sanity data
  const images = product.images || (product.image ? [product.image] : []);
  const mainImage = images[selectedImage] || product.image;
  const extras = Array.isArray(product.extras) ? product.extras : [];

  // Filter related products from cache for speed (or could fetch fresh if needed)
  // For simplicity, we just won't show related if we don't have global list handy easily inside this component
  // Or we can try to read cache again
  const getRelatedProducts = () => {
    try {
      const cached = localStorage.getItem('products_cache');
      if (cached) {
        const allProducts = JSON.parse(cached);
        return allProducts.filter(
          (p) => p.category === product.category && String(p.id) !== String(product.id)
        ).slice(0, 4);
      }
    } catch (e) { }
    return [];
  };
  const relatedProducts = getRelatedProducts();


  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-12">
        <div className="bg-white rounded-xl p-4 md:p-8 mb-8 shadow-sm">
          <div className="grid md:grid-cols-2 gap-8">
            <div ref={imageRef}>
              <div className="sticky top-24">
                <div className="aspect-[3/4] md:aspect-square rounded-xl overflow-hidden bg-gray-100 mb-4 border border-gray-100">
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    data-testid="product-main-image"
                  />
                </div>
                {images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index
                          ? 'border-trendyol-orange'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                        data-testid={`product-thumbnail-${index}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

              {!product?.isExtra && extras.length > 0 && (
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="font-semibold mb-3 text-lg">Yanında İyi Gider</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {extras.map((ex) => {
                      const xid = ex?.id;
                      const exUiId = ex?.ui_id;
                      if (xid == null || !exUiId) return null;
                      const xidStr = String(xid);
                      const checked = selectedExtraIds.includes(xidStr);
                      return (
                        <div key={String(exUiId)} className="rounded-xl border border-gray-200 bg-white p-3">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setPreviewExtra({ image: ex?.image || null, name: ex?.name || 'Ekstra' })}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left"
                            >
                              <div className="h-12 w-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                                {ex?.image ? (
                                  <img src={ex.image} alt={ex.name || ''} className="h-full w-full object-cover" />
                                ) : null}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">{ex?.name || 'Ekstra'}</div>
                                <div className="text-xs text-gray-600 truncate">{(Number(ex?.price) || 0).toFixed(2)} TL</div>
                              </div>
                            </button>

                            <input
                              type="checkbox"
                              className="h-5 w-5"
                              checked={checked}
                              onChange={(e) => {
                                const nextChecked = e.target.checked;
                                setSelectedExtraIds((prev) => {
                                  const arr = Array.isArray(prev) ? prev : [];
                                  if (nextChecked) return Array.from(new Set([...arr, xidStr]));
                                  return arr.filter((v) => String(v) !== xidStr);
                                });
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>
            </div>

            <div ref={detailsRef} className="space-y-6">
              <div>
                <h1 className="font-manrope font-bold text-2xl md:text-3xl mb-3 text-gray-900">
                  {product.name}
                </h1>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${i < Math.floor(product.rating || 0)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {product.rating || 0} ({product.reviews || 0} değerlendirme)
                    </span>
                  </div>
                </div>
                {product.seller && <p className="text-gray-600 mb-2 font-medium">Satıcı: <span className="text-trendyol-orange">{product.seller}</span></p>}
              </div>

              <div className="border-t border-b border-gray-100 py-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-manrope font-bold text-4xl text-trendyol-orange">
                    {product.price?.toFixed(2)} TL
                  </span>
                  {product.originalPrice && (
                    <>
                      <span className="text-xl text-gray-400 line-through">
                        {product.originalPrice.toFixed(2)} TL
                      </span>
                      <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded">
                        %{(product.discount_percent ?? product.discount ?? 0)} İNDİRİM
                      </span>
                    </>
                  )}
                </div>
                {product.isFreeShipping && (
                  <p className="text-green-600 font-medium flex items-center gap-2 bg-green-50 w-fit px-3 py-1 rounded-full">
                    <Truck className="w-5 h-5" />
                    Kargo Bedava
                  </p>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-3 text-lg">Ürün Açıklaması</h3>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">{product.description}</p>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-600">Adet:</span>
                <div className="flex items-center border border-gray-300 rounded-lg bg-white">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors text-gray-600"
                    data-testid="decrease-quantity"
                  >
                    -
                  </button>
                  <span className="px-4 py-2 font-semibold w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors text-gray-600"
                    data-testid="increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-trendyol-orange hover:bg-orange-600 text-white font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all hover:shadow-lg active:scale-95"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Sepete Ekle
                </button>
                <button
                  onClick={handleToggleFavorite}
                  className={`px-6 py-4 border-2 rounded-lg transition-colors ${isProductFavorite
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-trendyol-orange hover:text-trendyol-orange'
                    }`}
                  data-testid="add-to-favorites-button"
                >
                  <Heart className={`w-6 h-6 ${isProductFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-100 mt-6">
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-2 bg-orange-50 rounded-full flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                    <Truck className="w-6 h-6 text-trendyol-orange" />
                  </div>
                  <p className="text-xs font-medium text-gray-600">Hızlı Teslimat</p>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-2 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-green-100 transition-colors">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-xs font-medium text-gray-600">Güvenli Alışveriş</p>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-2 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <RotateCcw className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-xs font-medium text-gray-600">Kolay İade</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div>
            <h2 className="font-manrope font-bold text-2xl md:text-3xl mb-6 text-gray-900">
              Benzer Ürünler
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/urun/${relatedProduct.id}`}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 group"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <div className="aspect-[3/4] bg-gray-100 overflow-hidden">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium mb-2 line-clamp-2 h-10 text-gray-800 group-hover:text-trendyol-orange transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <p className="font-manrope font-bold text-trendyol-orange text-lg">
                        {relatedProduct.price.toFixed(2)} TL
                      </p>
                      {relatedProduct.originalPrice && (
                        <span className="text-xs text-gray-400 line-through">
                          {relatedProduct.originalPrice.toFixed(2)} TL
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {previewExtra?.image ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPreviewExtra(null)}
        >
          <div
            className="max-w-3xl w-full"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="font-semibold text-gray-900 truncate">{previewExtra?.name || ''}</div>
                <button
                  type="button"
                  className="text-gray-600 hover:text-gray-900"
                  onClick={() => setPreviewExtra(null)}
                >
                  Kapat
                </button>
              </div>
              <div className="bg-black">
                <img
                  src={previewExtra.image}
                  alt={previewExtra?.name || ''}
                  className="w-full h-auto object-contain max-h-[80vh]"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
